import { https } from "../http";
import store from "../store";
import { setUploadFileLoading } from "../store/reducers/document";

// Get a presigned POST for one upload (OD-1480).
// The backend builds the object key itself (`<category>/<provider>/<uuid>.<ext>`)
// and pins it, the Content-Type and a maximum size in the POST policy, so all
// we send is the file's name (for its extension) and type. `category` picks
// the top-level folder (e.g. 'provider_documents'); omit it for 'profile_photos'.
export const getUploadUrlApi = async ({ filename, type, category }) => {
  try {
    store.dispatch(setUploadFileLoading(true));
    const res = await https().get("/me/generate-presigned-url", {
      params: {
        filename,
        contentType: type,
        ...(category ? { category } : {}),
      },
    });
    return res?.data;
  } catch (error) {
    store.dispatch(setUploadFileLoading(false));
    console.error("Error fetching pre-signed URL", error);
    // Callers show error.message; give them the server's reason (e.g. "This
    // file type cannot be uploaded") rather than axios's status text.
    throw new Error(error?.response?.data?.message || error?.message || "Upload failed");
  } finally {
    store.dispatch(setUploadFileLoading(false));
  }
};

// Every policy field has to go in, unchanged and before the file, or S3
// rejects the POST.
export const postFileToS3Api = async ({ url, fields, file }) => {
  try {
    const form = new FormData();
    Object.entries(fields || {}).forEach(([name, value]) => form.append(name, value));
    form.append("file", file);
    return await fetch(url, { method: "POST", body: form });
  } catch (error) {
    console.error("Error uploading file to S3", error);
    throw error;
  }
};

const MB = 1024 * 1024;

// Master Upload Function. Every upload gets its own server-generated key, so
// two files can never overwrite each other; `category` is as above.
export const uploadFileToS3Api = async ({ file, category }) => {
  try {
    if (!file) throw new Error("No file provided");

    const { url, fields, key, fileUrl, maxBytes } = await getUploadUrlApi({
      filename: file.name || "file",
      type: file.type,
      category,
    });

    if (maxBytes && file.size > maxBytes) {
      throw new Error(`File is too large. The limit is ${Math.floor(maxBytes / MB)} MB.`);
    }

    const uploadRes = await postFileToS3Api({ url, fields, file });
    if (!uploadRes.ok) throw new Error("Failed to upload file to S3");
    return {
      message: "File uploaded successfully",
      uploadURL: fileUrl, // the stored object's URL
      path: key,
    };
  } catch (error) {
    console.error("uploadFileToS3Api Error:", error);
    throw error;
  }
};
